import Dexie, { Table } from "dexie";
// Only required for UUIDs.
import "dexie-observable";
import { defaultPhrases } from "./initial-data";

export interface AudioFile {
  id?: string;
  data: Blob;
  phrase: string;
  studentId: number;
  waveformImg?: string;
  sampleLength?: number;
}

export interface Student {
  id?: number;
  name: string;
  edited: boolean;
  number: number;
  gender?: string;
  phrases: string[];
  dataCollectionConsent: boolean;
}

export interface Phrase {
  id?: number;
  text: string;
  emoji?: string;
}

export class DexieDB extends Dexie {
  audioFiles!: Table<AudioFile>;
  students!: Table<Student>;
  phrases!: Table<Phrase>;

  constructor() {
    super("MLAudioDatabase");
    this.version(23).stores({
      audioFiles: "$$id, data, phrase, studentId, waveformImg, sampleLength",
      students:
        "++id, name, edited, number, gender, phrases, dataCollectionConsent",
      phrases: "++id, text, emoji",
    });
  }
}

export const db = new DexieDB();

export const DEFAULT_STUDENT_NAME = "Student";
export const DEFAULT_STUDENT_NUM = 10;

export const createStudents = async (
  numStudents: number = DEFAULT_STUDENT_NUM
): Promise<void> => {
  let studentNumber = 0;

  try {
    const currentStudents = await db.students.toArray();
    if (currentStudents.length > 0) {
      studentNumber = Math.max(
        ...currentStudents.map((student) => student.number)
      );
    }
  } catch (err) {
    console.error(`Failed to get students: ${err}`);
  }

  const students: Student[] = [];
  for (let i = 0; i < numStudents; i++) {
    studentNumber++;
    students.push({
      name: `${DEFAULT_STUDENT_NAME} ${studentNumber}`,
      phrases: [],
      number: studentNumber,
      edited: false,
      dataCollectionConsent: true,
    });
  }

  try {
    await db.students.bulkAdd(students);
  } catch (err) {
    console.error(`Failed to create students: ${err}`);
  }
};

export const createDefaultPhrases = async (): Promise<void> => {
  const phrases: Phrase[] = defaultPhrases.map((p) => ({
    text: p.text,
    emoji: p.emoji,
  }));
  try {
    await db.phrases.bulkAdd(phrases);
  } catch (err) {
    console.error(`Failed to create default phrases: ${err}`);
  }
};

export const createPhrase = async (
  phrase: string,
  emoji?: string
): Promise<void> => {
  try {
    await db.phrases.add({ text: phrase, emoji });
  } catch (err) {
    console.error(`Failed to create default phrases: ${err}`);
  }
};

export const checkForDuplicatePhrase = async (
  phrase: string
): Promise<boolean> => {
  let isDuplicate = false;
  try {
    const phrases = await db.phrases.toArray();
    const phrasesToLowerCase = phrases.map((phrase) =>
      phrase.text.toLowerCase()
    );
    if (phrasesToLowerCase.includes(phrase.toLowerCase())) {
      isDuplicate = true;
    }
  } catch (err) {
    console.error(`Failed to check for duplicate phrases: ${err}`);
  }
  return isDuplicate;
};

export const getActiveAudioFile = async (
  studentId: number,
  phrase: string
): Promise<AudioFile | undefined> => {
  try {
    const audioFiles = await db.audioFiles
      .filter((f) => f.studentId === studentId && f.phrase === phrase)
      .toArray();
    if (audioFiles.length === 1) {
      return audioFiles[0];
    }
  } catch (err) {
    console.error(`Failed to get files from database: ${err}`);
  }
};
